import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatErr, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { MarketDialog, ZoneDialog } from "./MarketZoneDialogs";
import { Plus, Pencil, Upload, Map as MapIcon } from "lucide-react";

const TONE = { active: "bg-[#9ACC32]/15 text-[#4A700A]", future: "bg-sky-100 text-sky-700", inactive: "bg-slate-200 text-slate-500" };
const Pill = ({ s }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${TONE[s] || TONE.inactive}`}>{s || "—"}</span>;

function ZoneRow({ z, isAdmin, onEdit, onUpload }) {
  return (
    <div className="grid gap-2 md:grid-cols-[220px_1fr_auto] items-start py-3 border-t border-slate-100" data-testid={`mz-zone-${z.id}`}>
      <div><p className="font-semibold text-slate-800 flex items-center gap-2">{z.name} <Pill s={z.status} /></p>
        <p className="text-[11px] text-slate-400 mt-0.5">Capacity {z.capacity ?? 32} · {z.salespeople?.length ? z.salespeople.map((s) => s.name).join(", ") : "No salesperson"}</p></div>
      <div className="text-xs text-slate-600">
        <span className={`mr-1.5 rounded px-1.5 py-0.5 text-[10px] font-bold ${z.boundary_status === "Confirmed" ? "bg-[#9ACC32]/15 text-[#4A700A]" : "bg-amber-100 text-amber-800"}`} data-testid={`mz-boundary-status-${z.id}`}>{z.boundary_status || "Draft — confirm"}</span>
        {z.boundary_description || <span className="text-slate-400">No boundary description yet.</span>}
        {z.notes && <p className="mt-1 text-slate-400">Notes: {z.notes}</p>}
      </div>
      <div className="flex items-center gap-1.5 justify-end">
        {z.map ? <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadBlob(`/library/${z.map.id}/download`, z.map.original_filename)} data-testid={`mz-map-view-${z.id}`}><MapIcon className="h-3 w-3" />Map</Button>
          : <span className="text-[11px] text-slate-400">{z.can_view_map ? "No map yet" : ""}</span>}
        {isAdmin && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onUpload(z)} data-testid={`mz-map-upload-${z.id}`}><Upload className="h-3 w-3" />{z.map ? "Replace" : "Upload"} map</Button>}
        {isAdmin && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(z)} data-testid={`mz-zone-edit-${z.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
      </div>
    </div>
  );
}

export default function MarketsZonesManager() {
  const { user } = useAuth();
  const isAdmin = ["tenant_owner", "platform_owner"].includes(user?.role);
  const [markets, setMarkets] = useState([]);
  const [reps, setReps] = useState([]);
  const [mDlg, setMDlg] = useState(undefined);
  const [zDlg, setZDlg] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const fileRef = useRef();
  const target = useRef(null);
  const load = useCallback(() => api.get("/mz/tree").then((r) => setMarkets(r.data.markets)), []);
  useEffect(() => { load(); if (isAdmin) api.get("/users").then((r) => setReps(r.data.items.filter((u) => u.role === "sales_rep" && u.invite_status !== "Cancelled"))); }, [load, isAdmin]);
  const pickMap = (z) => { target.current = z; fileRef.current?.click(); };
  const uploadMap = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/mz/zones/${target.current.id}/map`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Zone map saved to Document Library › Zone Maps (previous version kept)"); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const shown = markets.filter((m) => showInactive || m.status !== "inactive");

  return (
    <div className="space-y-4" data-testid="mz-manager">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-slate-500"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="mz-show-inactive" />Show inactive / historical markets</label>
        {isAdmin && <Button size="sm" onClick={() => setMDlg(null)} data-testid="mz-add-market"><Plus className="h-4 w-4" />Add Market</Button>}
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={uploadMap} data-testid="mz-map-input" />
      {shown.map((m) => {
        const zones = m.zones.filter((z) => showInactive || z.status !== "inactive");
        return (
          <div key={m.id} className="cc-card p-4" data-testid={`mz-market-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2"><h3 className="font-display font-semibold text-slate-800">{m.name}</h3><Pill s={m.status} /><span className="text-xs text-slate-400">{zones.length} zone{zones.length === 1 ? "" : "s"}</span></div>
              {isAdmin && <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setZDlg({ zone: null, marketId: m.id })} data-testid={`mz-add-zone-${m.id}`}><Plus className="h-3 w-3" />Zone</Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setMDlg(m)} data-testid={`mz-market-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button></div>}
            </div>
            {m.notes && <p className="text-xs text-slate-400 mt-1">{m.notes}</p>}
            <div className="mt-2">{zones.map((z) => <ZoneRow key={z.id} z={z} isAdmin={isAdmin} onEdit={(zz) => setZDlg({ zone: zz, marketId: m.id })} onUpload={pickMap} />)}</div>
          </div>
        );
      })}
      <MarketDialog market={mDlg || null} open={mDlg !== undefined} onOpenChange={(o) => !o && setMDlg(undefined)} onSaved={load} />
      <ZoneDialog zone={zDlg?.zone} marketId={zDlg?.marketId} markets={markets} reps={reps} open={!!zDlg} onOpenChange={(o) => !o && setZDlg(null)} onSaved={load} />
    </div>
  );
}

